import React, { useMemo } from 'react'
import { AlertCircle, LoaderCircleIcon } from 'lucide-react'

import { cn } from '@/lib/utils.ts'
import { Badge } from '@/components/ui/badge.tsx'
import { documentStatusLabels } from '@/features/documents/constants.ts'
import { Document } from '@/features/documents/types.ts'

export interface StatusBadgeProps<T extends Document = Document> {
  document?: T
  className?: string
}

export const StatusBadge: React.FC<StatusBadgeProps> = <T extends Document>(
  props: StatusBadgeProps<T>
) => {
  const { className, document } = props

  const icon = useMemo(() => {
    if (document?.status === 'processing') {
      return (
        <LoaderCircleIcon className="mr-2 size-4 animate-spin stroke-white" />
      )
    }

    return <AlertCircle className="mr-2 size-4 stroke-white" />
  }, [document?.status])

  if (!document) return null

  return (
    <Badge
      variant="secondary"
      className={cn(
        'bg-gray-400 p-1 px-2 text-white hover:bg-gray-400',
        document.status === 'in_review' && 'bg-yellow-400 hover:bg-yellow-400',
        document.status === 'ready' && 'bg-green-400 hover:bg-green-400',
        className
      )}
    >
      {icon}
      {documentStatusLabels[document.status]}
    </Badge>
  )
}
